import { defineStore } from "pinia";
import { z } from "zod";
import { CustomerAddressType } from "~/types/ecom/address.type";

const addressSchema = z.object({
  type: z.number().default(1),
  line_1: z.string().min(1, "House Number is required."),
  line_2: z.string().optional(),
  line_3: z.string().min(1, "Street is required."),
  town: z.string().min(1, "SignUp-placeReq"),
  postcode: z.string().regex(/^[0-9]{4} ?[A-Z]{2}$/, "SignUp-postalCodeInvalid"),
  country: z.string().optional(),
  country_id: z.number().int().positive(),
});

export const signUpSchema = z.object({
  title: z.string().min(1, "requiredTitle"),
  first_name: z.string().min(1, "requiredFirstName"),
  last_name: z.string().min(1, "requiredLastName"),
  email: z
    .string()
    .email("emailValidation")
    .min(1, "emailRequired"),
  mobile: z
    .string()
    .min(7, "mobileNumberValidation")
    .max(15, "mobileNumberValidation")
    .min(1, "mobileNumber"),
  password: z.string()
    .min(8, "passwordValidation")
    .max(30, "passwordValidation")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=(.*\d){2})(?=.*[\W_]).+$/,
      "passwordValidation"
  ),
  company: z.string().optional(),
  address: addressSchema,
  contact_preferences: z.object({
    email: z.boolean(),
    mobile: z.boolean(),
    sms: z.boolean(),
    post: z.boolean(),
  }),
  terms_accepted: z.boolean().refine((val) => val === true, {
    message: "youMustAcceptTerms",
  }),
});

export const guestSchema = z.object({
  first_name: z
    .string()
    .trim()
    .refine((value) => value.length >= 2, {
      message: "First name must be at least 2 characters long.",
    })
    .refine((value) => value.length <= 40, {
      message: "First name must not exceed 40 characters.",
    })
    .refine((value) => /^[a-zA-Z\s]+$/.test(value), {
      message: "First name can only contain letters and spaces.",
    })
    .refine((value) => value.length > 0, {
      message: "First name is required.",
    }),

  last_name: z
    .string()
    .trim()
    .refine((value) => value.length >= 2, {
      message: "Last name must be at least 2 characters long.",
    })
    .refine((value) => value.length <= 40, {
      message: "Last name must not exceed 40 characters.",
    })
    .refine((value) => /^[a-zA-Z\s]+$/.test(value), {
      message: "Last name can only contain letters and spaces.",
    })
    .refine((value) => value.length > 0, {
      message: "Last name is required.",
    }),

  email: z.string().email("Email must be valid."),

  mobile: z
    .string()
    .refine((value) => value.replace(/[^0-9]/g, "").length >= 5, {
      message: "Mobile number must be at least 5 digits long.",
    })
    .refine((value) => value.replace(/[^0-9]/g, "").length <= 30, {
      message: "Mobile number must not exceed 30 digits.",
    })
    .refine((value) => value.trim().length > 0, {
      message: "Mobile number is required.",
    }),

  address: addressSchema,
});

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    guest: null,
    jwt_token_data: null,
    error_message: <string | null>null,
    guest_error_message: <string | null>null,
    success_message: <string | null>null,
    is_guest_checkout: false,
    is_sign_in_button_loading: false,
    is_guest_sign_in_button_disabled: true,
    is_guest_sign_in_button_loading: false,
    is_forgot_password_button_loading: false,
    is_reset_password_button_loading: false,
    item: {
      username: "",
      password: "",
    },
    sign_in_form_error: {
      username: "",
      password: "",
    },
    forgot_password_item: {
      email: "",
    },
    forgot_password_error:{
      email:"",
    },
    reset_password_form_error:{
      token:"",
      email: "",
      password: "",
      password_confirmation: "",
    },
    guest_item: {
      email: "",
      first_name: "",
      last_name: "",
      mobile: "",
      terms_accepted: true,
      address: {
        type: CustomerAddressType.PRIMARY,
        town: "",
        postcode: "",
        line_1: "",
        line_2: "",
        line_3: "",
        country: "",
        country_id: 8,
      },
    },
    guest_form_error: {
      email: "",
      first_name: "",
      last_name: "",
      mobile: "",
      address: {
        type: CustomerAddressType.PRIMARY,
        town: "",
        postcode: "",
        line_1: "",
        line_2: "",
        line_3: "",
        country: "",
        country_id: 8,
      },
    },
    reset_password_item: {
      token: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
    active_form: "",
    sign_up_form: {
      title: "",
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      password: "",
      company: "",
      address: {
        type: CustomerAddressType.PRIMARY,
        town: "",
        postcode: "",
        line_1: "",
        line_2: "",
        line_3: "",
        country: "",
        country_id: 8,
      },
      contact_preferences: {
        email: false,
        mobile: false,
        sms: false,
        post: false,
      },
      terms_accepted: true,
    } as z.infer<typeof signUpSchema>,
    is_sign_up_button_loading: false,
    title_options: [
      { name: "Meneer", code: "Mr" },
      { name: "Mevrouw", code: "Mrs" },
    ],
    sign_up_form_error_message: "",
    sign_up_form_error: {
      title: "",
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      password: "",
      company: "",
      address: {
        type: CustomerAddressType.PRIMARY,
        town: "",
        postcode: "",
        line_1: "",
        line_2: "",
        line_3: "",
        country: "",
        country_id: 8,
      },
      contact_preferences: {
        email: false,
        mobile: false,
        sms: false,
        post: false,
      },
      terms_accepted: ""
    },
    sign_up_address_incomplete_error: "",
    disabled_toggler_items: ["sms", "email"],
    selectedPreferredLanguage: "",
    localeMap: [
      { code: 'nl-NL', name: 'Dutch (Nederland)' },
      { code: 'en-GB', name: 'English (United States)' },
      // { code : 'fr-FR', name: 'French (France)'},
      // { code : 'du-DU', name: 'Dutch (Belgium)'},
    ],
    isLoading: false,
    show_customer_info: false,
    rememberMe: false,
  }),
  persist: [
    {
      pick: [
        "guest.id",
        "guest.token",
        "guest_item.email",
        "guest_item.first_name",
        "guest_item.last_name",
        "guest_item.mobile",
        "is_guest_checkout",
      ],
    },
    {
      pick: ["guest"],
      storage: import.meta.client ? localStorage : undefined,
    },

  ],
  getters: {
    is_authenticated: (state): boolean => !(state.user == null),
    is_guest: (state): boolean => !(state.guest == null),
  },
  actions: {
    async setCustomerTokenInCookie(customer: any) {
      const { setCustomerTokenInCookie } = await import('~/stores-datalayer/auth.datalayer');
      return await setCustomerTokenInCookie( customer);
    },

    async setAuthUserByToken(access_token: string) {
      const { setAuthUserByToken } = await import('~/stores-datalayer/auth.datalayer');
      return await setAuthUserByToken(access_token);
      // return this.user;
    },

    async setUserDefaultBranchOnSignIn(defaultBranchId: string) {
      const { setUserDefaultBranchOnSignIn } = await import('~/stores-datalayer/auth.datalayer');
      return await setUserDefaultBranchOnSignIn(defaultBranchId);
    },

    async setUserDefaultBranchOnRefresh(defaultBranchId: string) {
      const { setUserDefaultBranchOnRefresh } = await import('~/stores-datalayer/auth.datalayer');
      return await setUserDefaultBranchOnRefresh(defaultBranchId);
    },

    async postSignIn() {
      const { postSignIn } = await import('~/stores-datalayer/auth.datalayer');
      return await postSignIn();
    },

    async checkUsername() {
      const { checkUsername } = await import('~/stores-datalayer/auth.datalayer');
      return await checkUsername();
    },

    async resetSignIn() {
      const { resetSignIn } = await import('~/stores-datalayer/auth.datalayer');
        resetSignIn();
    },

    async sendForgotPasswordMail() {
      const { sendForgotPasswordMail } = await import('~/stores-datalayer/auth.datalayer');
      return await sendForgotPasswordMail();
    },

    async postResetPassword() {
      const { postResetPassword } = await import('~/stores-datalayer/auth.datalayer');
      return await postResetPassword();
    },

    async signOut() {
      const { signOut } = await import('~/stores-datalayer/auth.datalayer');
      return await signOut();
    },

    handlePasswordValidationOnSignUp(passwordErrorKey: string) {  
      if(!this.sign_up_form.password) {
        this.sign_up_form_error.password = "requiredPassword";
        return;
      }
      this.sign_up_form_error.password = passwordErrorKey;
    },

    async signUp() {
      const { signUp } = await import('~/stores-datalayer/auth.datalayer');
      return await signUp(this);
    },

    async postSignUp() {
      const { postSignUp } = await import('~/stores-datalayer/auth.datalayer');
      return await postSignUp();
    },

    async postGuestSignUp() {
      const { postGuestSignUp } = await import('~/stores-datalayer/auth.datalayer');
      return await postGuestSignUp();
    },

    async clearSignupForm() {
      const { clearSignupForm } = await import('~/stores-datalayer/auth.datalayer');
      return clearSignupForm();
    },

    async validatePassword(password: string) {
      const { validatePassword } = await import('~/stores-datalayer/auth.datalayer');
      return validatePassword(password);
    },

    async validateEmail(email: string) {
      const { validateEmail } = await import('~/stores-datalayer/auth.datalayer');
      return validateEmail(email);
    },

    async getEmptySignInFields() {
      const { getEmptySignInFields } = await import('~/stores-datalayer/auth.datalayer');
      return getEmptySignInFields();
    },

    async getEmptySignUpFields() {
      const { getEmptySignUpFields } = await import('~/stores-datalayer/auth.datalayer');
      return getEmptySignUpFields();
    },

    async getStockNotifications() {
      const { getStockNotifications } = await import('~/stores-datalayer/auth.datalayer');
      return await getStockNotifications();
    },

    async onStockUnsubscribe(product_code: string) {
      const { onStockUnsubscribe } = await import('~/stores-datalayer/auth.datalayer');
      return await onStockUnsubscribe(product_code);
    },

    async fetchOneTimeQrCode(customerId: string) {
      const { fetchOneTimeQrCode } = await import('~/stores-datalayer/auth.datalayer');
      return await fetchOneTimeQrCode(customerId);
    },

    async fetchPreferredLanguage(customerId: string) {
      const { fetchPreferredLanguage } = await import('~/stores-datalayer/auth.datalayer');
      return await fetchPreferredLanguage(customerId);
    },

    async updatePreferredLanguage(customerId: string, localeCode: any) {
      const { updatePreferredLanguage } = await import('~/stores-datalayer/auth.datalayer');
      return await updatePreferredLanguage(customerId,localeCode);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
